import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalIcon, Clock, Users, Briefcase, Car, ArrowRight, Repeat, Plane, TrainFront, Timer, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationAutocomplete } from "@/components/LocationAutocomplete";
import { saveSearch } from "@/lib/constants";

const TABS = [
  { key: "local", label: "One Way", icon: ArrowRight },
  { key: "return", label: "Return", icon: Repeat },
  { key: "airport", label: "Airport", icon: Plane },
  { key: "station", label: "Station", icon: TrainFront },
  { key: "hourly", label: "Hourly", icon: Timer },
];

const VEHICLES = [
  ["any", "Any Vehicle"], ["saloon", "Saloon"], ["estate", "Estate"], ["mpv", "MPV"],
  ["executive", "Executive"], ["wheelchair", "Wheelchair Accessible"], ["sixseater", "6 Seater"],
  ["sevenseater", "7 Seater"], ["eightseater", "8 Seater"],
];
const HOURS = [2, 4, 6, 8, 12];

export function BookingWidget({ compact = false, initialTab = "local" }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("09:00");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [smallBags, setSmallBags] = useState(0);
  const [largeBags, setLargeBags] = useState(0);
  const [vehicle, setVehicle] = useState("any");
  const [hours, setHours] = useState(4);
  const [returnDate, setReturnDate] = useState(today);
  const [returnTime, setReturnTime] = useState("18:00");
  const [error, setError] = useState("");

  const isHourly = tab === "hourly";

  const submit = () => {
    if (!pickup) { setError("Please choose a pickup location"); return; }
    if (!isHourly && !dropoff) { setError("Please choose a destination"); return; }
    setError("");
    const serviceType = tab === "return" ? "local" : tab;
    saveSearch({
      service_type: serviceType, tab, pickup, dropoff: isHourly ? null : dropoff,
      date, time, passengers: adults + children,
      passengers_detail: { adults, children, infants },
      luggage: smallBags + largeBags, luggage_detail: { small: smallBags, large: largeBags },
      vehicle_key: vehicle, hours: isHourly ? hours : null, wheelchair: vehicle === "wheelchair",
      return_date: tab === "return" ? returnDate : null, return_time: tab === "return" ? returnTime : null,
    });
    navigate("/search?go=1");
  };

  const Counter = ({ label, value, set, min = 0, testId }) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`${testId}-dec`} onClick={() => set(Math.max(min, value - 1))} className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100">−</button>
        <span className="w-5 text-center text-sm font-semibold" data-testid={`${testId}-val`}>{value}</span>
        <button type="button" data-testid={`${testId}-inc`} onClick={() => set(value + 1)} className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100">+</button>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:p-5">
      <div className="mb-4 flex gap-1 overflow-x-auto no-scrollbar rounded-xl bg-slate-100 p-1">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`booking-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition ${tab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3">
        <div className={`grid gap-3 ${!isHourly ? "sm:grid-cols-2" : ""}`}>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Pickup</label>
            <LocationAutocomplete value={pickup} onSelect={setPickup} placeholder="Enter pickup location" testId="pickup-input" />
          </div>
          {!isHourly && (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Drop-off</label>
              <LocationAutocomplete value={dropoff} onSelect={setDropoff} placeholder="Where are you going?" testId="dropoff-input" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Date</label>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 h-12">
              <CalIcon size={16} className="text-brand" />
              <input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" className="w-full bg-transparent text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Time</label>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 h-12">
              <Clock size={16} className="text-brand" />
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="time-input" className="w-full bg-transparent text-sm outline-none" />
            </div>
          </div>
          {isHourly ? (
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-500">Duration</label>
              <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
                <SelectTrigger className="h-12" data-testid="hours-select"><Timer size={16} className="text-brand" /><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{h} Hours</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-500">Vehicle preference</label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger className="h-12" data-testid="vehicle-select"><Car size={16} className="text-brand" /><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        {tab === "return" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Return date</label>
              <input type="date" min={date} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="return-date" className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Return time</label>
              <input type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} data-testid="return-time" className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none" />
            </div>
          </div>
        )}

        {!compact && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Counter label="Adults" value={adults} set={setAdults} min={1} testId="adults" />
            <Counter label="Children" value={children} set={setChildren} testId="children" />
            <Counter label="Infants" value={infants} set={setInfants} testId="infants" />
            <Counter label="Small bags" value={smallBags} set={setSmallBags} testId="smallbags" />
            <Counter label="Large bags" value={largeBags} set={setLargeBags} testId="largebags" />
          </div>
        )}

        {error && <p className="text-sm text-red-600" data-testid="widget-error">{error}</p>}

        <Button size="lg" onClick={submit} data-testid="search-taxis-btn" className="h-13 w-full gap-2 bg-brand py-6 text-base font-semibold hover:bg-brand/90">
          <MapPin size={18} /> Search Available Taxis
        </Button>
      </div>
    </div>
  );
}
